import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

import API_BASE_URL from "../config/api";


function EditSubject() {

    const { id } = useParams();

    const navigate = useNavigate();


    /*
    |--------------------------------------------------------------------------
    | Form Data
    |--------------------------------------------------------------------------
    */

    const [formData, setFormData] = useState({
        subject_name: "",
        status: "Enable",
        iconimage: null
    });


    /*
    |--------------------------------------------------------------------------
    | Existing Image
    |--------------------------------------------------------------------------
    */

    const [existingImage, setExistingImage] = useState(null);

    const [imagePreview, setImagePreview] = useState(null);


    /*
    |--------------------------------------------------------------------------
    | Loading States
    |--------------------------------------------------------------------------
    */

    const [loading, setLoading] = useState(true);

    const [saving, setSaving] = useState(false);


    /*
    |--------------------------------------------------------------------------
    | Get Subject Details
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        const fetchSubject = async () => {

            try {

                const response = await fetch(
                    `${API_BASE_URL}/admin/subjects/${id}`,
                    {
                        method: "GET",

                        headers: {
                            "Accept": "application/json"
                        },

                        credentials: "include"
                    }
                );


                const data = await response.json();


                console.log("Edit Subject API Response:", data);


                if (response.ok && data.success) {

                    const subject = data.subject || {};


                    /*
                    |--------------------------------------------------------------------------
                    | Existing Image URL
                    |--------------------------------------------------------------------------
                    */

                    let imageUrl = null;


                    if (subject.iconimage_url) {

                        imageUrl = subject.iconimage_url;

                    } else if (subject.iconimage) {

                        const baseUrl = API_BASE_URL.replace(
                            /\/api\/?$/,
                            ""
                        );

                        imageUrl =
                            `${baseUrl}/storage/subject/${encodeURIComponent(
                                subject.iconimage
                            )}`;
                    }


                    /*
                    |--------------------------------------------------------------------------
                    | Set Form Data
                    |--------------------------------------------------------------------------
                    */

                    setFormData({
                        subject_name: subject.subject_name || "",
                        status: subject.status || "Enable",
                        iconimage: null
                    });


                    /*
                    |--------------------------------------------------------------------------
                    | Set Existing Image
                    |--------------------------------------------------------------------------
                    */

                    setExistingImage(imageUrl);

                    setImagePreview(imageUrl);

                } else {

                    Swal.fire({
                        icon: "error",
                        title: "Unable to Load Subject",
                        text: data.message || "Subject not found."
                    });

                    navigate("/subjects");
                }

            } catch (error) {

                console.error("Fetch Subject Error:", error);

                Swal.fire({
                    icon: "error",
                    title: "Server Error",
                    text: "Unable to connect with server."
                });

                navigate("/subjects");

            } finally {

                setLoading(false);
            }
        };


        fetchSubject();

    }, [id, navigate]);


    /*
    |--------------------------------------------------------------------------
    | Handle Input Change
    |--------------------------------------------------------------------------
    */

    const handleChange = (e) => {

        const { name, value } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));
    };


    /*
    |--------------------------------------------------------------------------
    | Handle Image Change
    |--------------------------------------------------------------------------
    */

    const handleImageChange = (e) => {

        const file = e.target.files?.[0];

        if (!file) {
            return;
        }


        /*
        |--------------------------------------------------------------------------
        | Allowed Image Types
        |--------------------------------------------------------------------------
        */

        const allowedTypes = [
            "image/jpeg",
            "image/jpg",
            "image/png",
            "image/webp",
            "image/gif"
        ];


        if (!allowedTypes.includes(file.type)) {

            Swal.fire({
                icon: "warning",
                title: "Invalid Image",
                text: "Only JPG, JPEG, PNG, WEBP and GIF images are allowed."
            });

            e.target.value = "";

            return;
        }


        /*
        |--------------------------------------------------------------------------
        | Maximum 2 MB
        |--------------------------------------------------------------------------
        */

        if (file.size > 2 * 1024 * 1024) {

            Swal.fire({
                icon: "warning",
                title: "Image Too Large",
                text: "Image size must be less than 2 MB."
            });

            e.target.value = "";

            return;
        }


        /*
        |--------------------------------------------------------------------------
        | Set New Image
        |--------------------------------------------------------------------------
        */

        setFormData((prev) => ({
            ...prev,
            iconimage: file
        }));


        /*
        |--------------------------------------------------------------------------
        | Preview
        |--------------------------------------------------------------------------
        */

        const previewUrl = URL.createObjectURL(file);

        setImagePreview(previewUrl);
    };


    /*
    |--------------------------------------------------------------------------
    | Update Subject
    |--------------------------------------------------------------------------
    */

    const handleSubmit = async (e) => {

        e.preventDefault();


        /*
        |--------------------------------------------------------------------------
        | Subject Name Validation
        |--------------------------------------------------------------------------
        */

        if (!formData.subject_name.trim()) {

            Swal.fire({
                icon: "warning",
                title: "Subject Name Required",
                text: "Please enter subject name."
            });

            return;
        }


        try {

            setSaving(true);


            /*
            |--------------------------------------------------------------------------
            | FormData
            |--------------------------------------------------------------------------
            */

            const data = new FormData();


            data.append(
                "subject_name",
                formData.subject_name.trim()
            );


            data.append(
                "status",
                formData.status
            );


            /*
            |--------------------------------------------------------------------------
            | New Image
            |
            | Image is optional during edit.
            | If user does not select a new image,
            | old image will remain unchanged.
            |--------------------------------------------------------------------------
            */

            if (formData.iconimage) {

                data.append(
                    "iconimage",
                    formData.iconimage
                );
            }


            /*
            |--------------------------------------------------------------------------
            | Laravel Method Spoofing
            |--------------------------------------------------------------------------
            */

            data.append("_method", "PUT");


            /*
            |--------------------------------------------------------------------------
            | API Request
            |--------------------------------------------------------------------------
            */

            const response = await fetch(
                `${API_BASE_URL}/admin/subjects/${id}`,
                {
                    method: "POST",

                    headers: {
                        "Accept": "application/json"
                    },

                    credentials: "include",

                    body: data
                }
            );


            const result = await response.json();


            console.log(
                "Update Subject API Response:",
                result
            );


            /*
            |--------------------------------------------------------------------------
            | Success
            |--------------------------------------------------------------------------
            */

            if (response.ok && result.success) {

                await Swal.fire({
                    icon: "success",
                    title: "Updated!",
                    text:
                        result.message ||
                        "Subject updated successfully.",
                    timer: 1500,
                    showConfirmButton: false
                });


                navigate("/subjects");

            } else {

                /*
                |--------------------------------------------------------------------------
                | Laravel Validation Errors
                |--------------------------------------------------------------------------
                */

                let errorMessage =
                    result.message ||
                    "Something went wrong.";


                if (result.errors) {

                    const validationErrors = Object.values(
                        result.errors
                    )
                        .flat()
                        .join("\n");


                    if (validationErrors) {

                        errorMessage = validationErrors;
                    }
                }


                Swal.fire({
                    icon: "error",
                    title: "Unable to Update Subject",
                    text: errorMessage
                });
            }

        } catch (error) {

            console.error(
                "Update Subject Error:",
                error
            );


            Swal.fire({
                icon: "error",
                title: "Server Error",
                text: "Unable to connect with server."
            });

        } finally {

            setSaving(false);
        }
    };


    /*
    |--------------------------------------------------------------------------
    | Loading Screen
    |--------------------------------------------------------------------------
    */

    if (loading) {

        return (

            <div className="container-fluid">

                <div className="text-center py-5">

                    <div
                        className="spinner-border text-primary"
                        role="status"
                    >
                    </div>

                    <p className="mt-2">
                        Loading subject...
                    </p>

                </div>

            </div>
        );
    }


    /*
    |--------------------------------------------------------------------------
    | UI
    |--------------------------------------------------------------------------
    */

    return (

         <div
            className="container-fluid py-4"
            style={{
                maxHeight: "500px",
                overflowX: "auto",
                overflowY: "auto"
            }}
        >

            {/* Page Header */}

            <div className="d-flex justify-content-between align-items-center mb-4">

                <h3 className="mb-0">
                    Edit Subject
                </h3>


                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => navigate("/subjects")}
                    disabled={saving}
                >
                    Back
                </button>

            </div>


            {/* Form Card */}

            <div className="card shadow-sm">

                <div className="card-body">

                    <form onSubmit={handleSubmit}>


                        {/* Subject Name */}

                        <div className="mb-3">

                            <label className="form-label">
                                Subject Name
                            </label>


                            <input
                                type="text"
                                name="subject_name"
                                className="form-control"
                                placeholder="Enter subject name"
                                value={formData.subject_name}
                                onChange={handleChange}
                                maxLength="100"
                                disabled={saving}
                            />

                        </div>


                        {/* Subject Icon */}

                        <div className="mb-3">

                            <label className="form-label">
                                Subject Icon
                            </label>


                            <input
                                type="file"
                                className="form-control"
                                accept=".jpg,.jpeg,.png,.webp,.gif,image/jpeg,image/png,image/webp,image/gif"
                                onChange={handleImageChange}
                                disabled={saving}
                            />


                            <small className="text-muted">
                                Leave empty to keep the existing icon.
                                JPG, JPEG, PNG, WEBP or GIF. Maximum 2 MB.
                            </small>

                        </div>


                        {/* Image Preview */}

                        {imagePreview && (

                            <div className="mb-4">

                                <label className="form-label d-block">
                                    Icon Preview
                                </label>


                                <div
                                    className="border rounded p-2 d-inline-block"
                                    style={{
                                        backgroundColor: "#f8f9fa"
                                    }}
                                >

                                    <img
                                        src={imagePreview}
                                        alt="Subject Icon"
                                        style={{
                                            width: "100px",
                                            height: "100px",
                                            objectFit: "cover",
                                            display: "block"
                                        }}
                                        onError={(e) => {

                                            console.error(
                                                "Subject image failed:",
                                                {
                                                    existingImage,
                                                    imagePreview,
                                                    formData
                                                }
                                            );

                                            e.currentTarget.style.display =
                                                "none";
                                        }}
                                    />

                                </div>

                            </div>
                        )}


                        {/* Status */}

                        <div className="mb-3">

                            <label className="form-label">
                                Status
                            </label>


                            <select
                                name="status"
                                className="form-select"
                                value={formData.status}
                                onChange={handleChange}
                                disabled={saving}
                            >

                                <option value="Enable">
                                    Enable
                                </option>

                                <option value="Disable">
                                    Disable
                                </option>

                            </select>

                        </div>


                        {/* Buttons */}

                        <div className="d-flex gap-2">

                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={saving}
                            >

                                {saving
                                    ? "Updating..."
                                    : "Update Subject"
                                }

                            </button>


                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => navigate("/subjects")}
                                disabled={saving}
                            >
                                Cancel
                            </button>

                        </div>

                    </form>

                </div>

            </div>

        </div>
    );
}


export default EditSubject;
